/**
 * Runtime coercion helpers.
 *
 * The Quasar app used `R.is(Number, data.x) ? data.x : null` all through its
 * `src/constructors`. That pattern exists because the API returns partially
 * populated records — a field is either the right type or absent — and the UI
 * reads it unconditionally. The behaviour is preserved exactly; ramda is not,
 * because five two-line guards are cheaper than the dependency.
 */

export type Raw = Record<string, unknown>

/** Anything that came off the wire, defensively narrowed to an object. */
export function asRaw(value: unknown): Raw {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Raw) : {}
}

export function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

export function asBoolean(value: unknown, fallback: boolean | null = null): boolean | null {
  return typeof value === 'boolean' ? value : fallback
}

/**
 * The API sends timestamps as ISO strings. `R.is(String, x) ? new Date(x)`
 * happily produced `Invalid Date` for malformed input; this rejects those
 * instead, which is what every call site already assumed.
 */
export function asDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value !== 'string') return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function asArray<T = unknown>(value: unknown): T[] | null {
  return Array.isArray(value) ? (value as T[]) : null
}

export function asStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null
  return value.filter((item): item is string => typeof item === 'string')
}

export function asObject(value: unknown): Raw | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Raw)
    : null
}

/**
 * Drops null/undefined entries, mirroring the `payload()` method the Giveaway
 * constructor used before POSTing.
 */
export function withoutNullish<T extends object>(input: T): Partial<T> {
  const out: Partial<T> = {}
  for (const [key, value] of Object.entries(input)) {
    if (value !== null && value !== undefined) {
      out[key as keyof T] = value as T[keyof T]
    }
  }
  return out
}
